import type { LucideIcon } from 'lucide-react';
import {
  ChartColumn,
  LayoutDashboard,
  LifeBuoy,
  Megaphone,
  MessageSquare,
  Package,
  Plug,
  Settings,
  ShoppingCart,
  FileText,
  Users,
} from 'lucide-react';
import { ROUTES } from '@/routes/paths';

/**
 * Elementos de la navegación lateral.
 *
 * Estructura copiada de la referencia visual: un grupo principal (el día a día del
 * negocio), un separador, y un grupo secundario de configuración/ayuda. El estado
 * "activo" lo calcula `NavLink` de React Router.
 *
 * `endpoints` documenta qué rutas del backend alimentan cada sección (según
 * docs/openapi.yaml). Si un elemento dice "pendiente de endpoint", el backend todavía no
 * expone lo necesario para construirlo — se deja visible para no tener que reordenar la
 * navegación después.
 */

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Coincidencia exacta de ruta (para que `/` no marque todo como activo). */
  end?: boolean;
}

export interface NavSection {
  id: string;
  /** Título opcional del grupo (la referencia solo pone separador). */
  label?: string;
  items: NavItem[];
}

export const navSections: NavSection[] = [
  {
    id: 'operacion',
    items: [
      { label: 'Panel', to: ROUTES.dashboard, icon: LayoutDashboard, end: true },
      // Inbox: los endpoints de lectura existen desde el módulo `conversations`
      // (ver docs/INBOX.md). El handoff sigue en `PATCH .../conversations/:id/bot` (módulo 4).
      { label: 'Conversaciones', to: ROUTES.conversations, icon: MessageSquare },
      { label: 'Órdenes', to: ROUTES.orders, icon: ShoppingCart },
      { label: 'Productos', to: ROUTES.products, icon: Package },
      { label: 'CRM', to: ROUTES.crm, icon: Users },
      { label: 'Campañas', to: ROUTES.campaigns, icon: Megaphone },
      { label: 'Plantillas', to: ROUTES.templates, icon: FileText },
      { label: 'Reportes', to: ROUTES.reports, icon: ChartColumn },
    ],
  },
  {
    id: 'configuracion',
    items: [
      { label: 'Integraciones', to: ROUTES.integrations, icon: Plug },
      { label: 'Ajustes', to: ROUTES.settings, icon: Settings },
      { label: 'Ayuda', to: ROUTES.help, icon: LifeBuoy },
    ],
  },
];

/** Ítem de la navegación que corresponde a una ruta (para el título del header). */
export function findNavItem(pathname: string): NavItem | null {
  const allItems = navSections.flatMap((section) => section.items);

  // Coincidencia exacta primero; si no, por prefijo (las sub-rutas mantienen el título
  // de su sección, ej. /settings/payment-methods → "Ajustes").
  return (
    allItems.find((item) => item.to === pathname) ??
    allItems
      .filter((item) => pathname.startsWith(`${item.to}/`))
      .sort((a, b) => b.to.length - a.to.length)[0] ??
    null
  );
}
